import { Component } from '@theme/component';

/**
 * Tab switcher for the product-page tab block.
 *
 * Plain tabs rather than Horizon's accordion, because the design calls for a filled active
 * tab above a bordered panel. Keyboard support follows the WAI-ARIA tabs pattern: arrows
 * move between tabs, Home and End jump to the ends.
 *
 * @extends {Component}
 */
class CivitalTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {string} name */
  #select(name) {
    for (const tab of this.tabs) {
      const isActive = tab.dataset.tab === name;

      tab.classList.toggle('civital-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      // Roving tabindex: only the selected tab is in the tab order, per WAI-ARIA.
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      /** @type {HTMLElement} */ (panel).hidden =
        /** @type {HTMLElement} */ (panel).dataset.panel !== name;
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');

    if (tab instanceof HTMLElement && tab.dataset.tab) this.#select(tab.dataset.tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.tabs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));

    if (current === -1) return;

    let next = current;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[next].focus();

    if (tabs[next].dataset.tab) this.#select(tabs[next].dataset.tab);
  };
}

if (!customElements.get('civital-tabs')) {
  customElements.define('civital-tabs', CivitalTabs);
}
